import { useCallback, useEffect, useState } from "react";
import { Search, Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";

const fmt = (at) => new Date(at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const ROLE = { admin: "Admin", moderator: "Moderator" };

export function WarningsAdmin() {
  const [d, setD] = useState(null);
  const [by, setBy] = useState("");
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/admin/warnings", { params: { ...(by ? { by } : {}), ...(q.trim() ? { q } : {}) } }).then(({ data }) => setD(data)), [by, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const remove = async (w) => {
    if (!window.confirm(`Verwarnung von ${w.user?.name || "diesem Mitglied"} löschen?\n„${w.reason}“`)) return;
    try { await api.delete(`/admin/warnings/${w.id}`); toast.success("Verwarnung gelöscht"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="space-y-4" data-testid="admin-warnings">
      <div className="bg-white rounded-3xl border border-pink-100 p-5 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Verwarnungen <span className="text-sm font-semibold text-slate-400" data-testid="admin-warnings-total">({d.total} insgesamt)</span></h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nutzer suchen (Name oder E-Mail)" data-testid="admin-warnings-search" className="w-full h-11 pl-11 pr-4 rounded-full border border-pink-100 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
          </div>
          <select value={by} onChange={(e) => setBy(e.target.value)} data-testid="admin-warnings-moderator" className="h-11 px-4 rounded-full border border-pink-100 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-200">
            <option value="">Alle Moderatoren & Admins</option>
            {d.moderators.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} ({ROLE[m.role] || m.role}) · {m.count}</option>)}
          </select>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-pink-100 p-5">
        <p className="text-sm text-slate-500 mb-3" data-testid="admin-warnings-count">{d.warnings.length} {d.warnings.length === 1 ? "Verwarnung" : "Verwarnungen"} gefunden</p>
        {d.warnings.length === 0 ? <p className="text-sm text-slate-400">Keine Verwarnungen für diesen Filter.</p> : (
          <ul className="divide-y divide-pink-50">
            {d.warnings.map((w) => (
              <li key={w.id} className="py-3 flex gap-3" data-testid={`admin-warning-${w.id}`}>
                <UserAvatar userId={w.user_id} name={w.user?.name} picture={w.user?.picture} size="w-10 h-10" crown={false} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm"><UserLink id={w.user_id} className="font-bold">{w.user?.name || "Gelöschter Nutzer"}</UserLink>
                    <span className={`ml-2 text-[11px] font-extrabold px-2 py-0.5 rounded-full ${w.user_total >= 3 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`admin-warning-total-${w.id}`}>{w.user_total}× verwarnt</span></p>
                  <p className="text-sm text-slate-700 mt-0.5 break-words">{w.reason}</p>
                  <p className="text-xs text-slate-400 mt-0.5">von {w.by_name} ({ROLE[w.by_role] || w.by_role}) · {fmt(w.created_at)}</p>
                </div>
                <button onClick={() => remove(w)} title="Verwarnung löschen" data-testid={`admin-warning-delete-${w.id}`} className="self-start w-9 h-9 shrink-0 rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
